import type { ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router-dom';

import { useSession } from './session.tsx';

/**
 * Espace à onglets — plusieurs écrans qui traitent d'une même matière.
 *
 * Le stock se suivait sur quatre entrées de menu séparées : l'état, les
 * réceptions, les transferts, les inventaires. Ce sont quatre vues d'UNE
 * question — « qu'est-ce que j'ai, et d'où ça vient » — et les enchaîner
 * obligeait à repasser par la navigation à chaque fois.
 *
 * ⚠️ Les URL ne changent PAS. `/receptions` reste `/receptions`, et non
 * `/stock/receptions` : des signets existent, et les tests de bout en bout
 * s'y rendent directement. Un regroupement d'interface n'a pas à casser des
 * adresses qui fonctionnent.
 *
 * C'est pour cela que la route est SANS CHEMIN (`<Route element={…}>`) : elle
 * enveloppe ses enfants sans rien préfixer.
 */

export interface Onglet {
  vers: string;
  libelle: string;
  permission?: string;
}

export function EspaceOnglets({
  titre,
  onglets,
}: {
  /** Nommé pour les lecteurs d'écran : sans cela, deux jeux d'onglets sur
      le site s'annoncent tous deux « navigation ». */
  titre: string;
  onglets: Onglet[];
}): ReactNode {
  const { peut } = useSession();

  const visibles = onglets.filter(
    (onglet) => onglet.permission === undefined || peut(onglet.permission),
  );

  return (
    <>
      {/* Un seul onglet autorisé : la barre n'apporte rien et disparaît. */}
      {visibles.length > 1 && (
        <nav className="onglets" aria-label={titre}>
          {visibles.map((onglet) => (
            <NavLink
              key={onglet.vers}
              to={onglet.vers}
              end
              className={({ isActive }) =>
                isActive ? 'onglet onglet--actif' : 'onglet'
              }
            >
              {onglet.libelle}
            </NavLink>
          ))}
        </nav>
      )}

      <Outlet />
    </>
  );
}

/**
 * Les quatre vues du stock.
 *
 * « État » plutôt que « Stock » : l'onglet vit déjà sous un titre qui dit
 * stock, le répéter n'apprend rien.
 */
export const ONGLETS_STOCK: Onglet[] = [
  { vers: '/stock', libelle: 'État', permission: 'stock.lire' },
  { vers: '/receptions', libelle: 'Entrée en stock', permission: 'stock.lire' },
  { vers: '/transferts', libelle: 'Transferts', permission: 'stock.lire' },
  { vers: '/inventaires', libelle: 'Inventaires', permission: 'inventaire.valider' },
];

/**
 * Les deux vues des résultats : les agrégats par boutique, et le détail par
 * article qui explique ces agrégats.
 */
export const ONGLETS_RESULTATS: Onglet[] = [
  { vers: '/resultats', libelle: 'Résultat global', permission: 'rapport.ca.lire' },
  { vers: '/resultats/detaille', libelle: 'Résultats détaillé', permission: 'rapport.ca.lire' },
];
